import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Table,
  Tabs,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PaidIcon from "@mui/icons-material/Paid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  CHECKER_DISBURSE_LOAN,
  CHECKER_VERIFY_LOAN,
  MAKER_ACCEPT_LOAN,
  MAKER_REJECT_LOAN,
} from "apollo/mutuations";
import { LOAN_APPLICATIONS, LOAN_CUSTOMER } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { rolePermissions } from "utiles/permissions";

const LoanApplications = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [acceptModalOpened, setAcceptModalOpened] = useState(false);
  const [applicationToAccept, setApplicationToAccept] = useState(null);
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [applicationToReject, setApplicationToReject] = useState(null);
  const [rejectRemark, setRejectRemark] = useState(
    "Required application documents are missing.",
  );
  const [verifyModalOpened, setVerifyModalOpened] = useState(false);
  const [applicationToVerify, setApplicationToVerify] = useState(null);
  const [checkerRemark, setCheckerRemark] = useState("Application verified.");
  const [disburseModalOpened, setDisburseModalOpened] = useState(false);
  const [applicationToDisburse, setApplicationToDisburse] = useState(null);
  const [makerRemark, setMakerRemark] = useState(
    "Application reviewed and accepted for checker review.",
  );
  const [viewData, setViewData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState(null);
  const [workflowFilter, setWorkflowFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  const { data, loading, refetch } = useQuery(LOAN_APPLICATIONS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      status: statusFilter,
      workflow_status: workflowFilter,
      orderBy: [
        {
          column: "created_at",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Get Loan Applications",
      });
    },
  });

  // Query for customer profile
  const {
    data: customerData,
    loading: customerLoading,
    refetch: refetchCustomer,
  } = useQuery(LOAN_CUSTOMER, {
    variables: {
      user_id: viewData?.user_id,
    },
    skip: !viewData?.user_id || activeTab !== "customer",
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message ||
          "Failed to Get Customer Profile",
      });
    },
  });

  const isMaker = rolePermissions.hasMaker(
    JSON.parse(localStorage.getItem("roles")) || [],
  );
  const isChecker = rolePermissions.hasChecker(
    JSON.parse(localStorage.getItem("roles")) || [],
  );
  const [makerAcceptLoan, { loading: acceptingLoan }] = useMutation(
    MAKER_ACCEPT_LOAN,
    {
      onCompleted() {
        showNotification({
          color: "green",
          title: "Application accepted",
          message: "The application was submitted for checker review.",
        });
        setAcceptModalOpened(false);
        setApplicationToAccept(null);
        setMakerRemark("");
        refetch();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to accept loan application",
        });
      },
    },
  );

  const handleAcceptApplication = () => {
    if (!applicationToAccept) return;

    makerAcceptLoan({
      variables: {
        application_id: applicationToAccept.id,
        remark: makerRemark.trim() || null,
      },
    });
  };
  const [makerRejectLoan, { loading: rejectingLoan }] = useMutation(
    MAKER_REJECT_LOAN,
    {
      onCompleted() {
        showNotification({
          color: "green",
          title: "Application rejected",
          message: "The application was rejected.",
        });
        setRejectModalOpened(false);
        setApplicationToReject(null);
        setRejectRemark("");
        refetch();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to reject loan application",
        });
      },
    },
  );

  const handleRejectApplication = () => {
    const remark = rejectRemark.trim();
    if (!applicationToReject || !remark) return;

    makerRejectLoan({
      variables: {
        application_id: applicationToReject.id,
        remark,
      },
    });
  };
  const [checkerVerifyLoan, { loading: verifyingLoan }] = useMutation(
    CHECKER_VERIFY_LOAN,
    {
      onCompleted() {
        showNotification({
          color: "green",
          title: "Application verified",
          message: "The application was verified by the checker.",
        });
        setVerifyModalOpened(false);
        setApplicationToVerify(null);
        setCheckerRemark("");
        refetch();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to verify loan application",
        });
      },
    },
  );

  const handleVerifyApplication = () => {
    if (!applicationToVerify) return;

    checkerVerifyLoan({
      variables: {
        application_id: applicationToVerify.id,
        remark: checkerRemark.trim() || null,
      },
    });
  };
  const [checkerDisburseLoan, { loading: disbursingLoan }] = useMutation(
    CHECKER_DISBURSE_LOAN,
    {
      onCompleted() {
        showNotification({
          color: "green",
          title: "Loan disbursed",
          message: "The loan was disbursed successfully.",
        });
        setDisburseModalOpened(false);
        setApplicationToDisburse(null);
        refetch();
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to disburse loan",
        });
      },
    },
  );

  const handleDisburseApplication = () => {
    if (!applicationToDisburse) return;

    checkerDisburseLoan({
      variables: {
        application_id: applicationToDisburse.id,
      },
    });
  };

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanApplications.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const theme = useMantineTheme();

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "green";
      case "PENDING":
        return "yellow";
      case "REJECTED":
        return "red";
      case "UNDER_REVIEW":
        return "blue";
      case "MAKER_SUBMITTED":
        return "cyan";
      case "CHECKER_VERIFIED":
        return "teal";
      case "CHECKER_RETURNED":
        return "orange";
      case "DISBURSED":
        return "violet";
      default:
        return "gray";
    }
  };

  const getWorkflowStatusColor = (status) => {
    switch (status) {
      case "MAKER_SUBMITTED":
        return "blue";
      case "CHECKER_VERIFIED":
        return "cyan";
      case "CHECKER_RETURNED":
        return "orange";
      case "APPROVED":
      case "APPROVER_APPROVED":
        return "green";
      case "CHECKER_REJECTED":
      case "APPROVER_REJECTED":
      case "REJECTED":
        return "red";
      default:
        return "gray";
    }
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "TRX",
      key: "trx",
      width: "25%",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span style={{ fontWeight: 500 }}>{rowData.trx}</span>;
      },
    },
    {
      label: "Product",
      key: "product_name",
      sortable: false,
      searchable: true,
      width: "15%",
      render: (rowData) => {
        return <span>{rowData.product_name || "N/A"}</span>;
      },
    },
    {
      label: "Borrower Type",
      key: "borrower_type",
      sortable: false,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return <span>{rowData.borrower_type || "N/A"}</span>;
      },
    },
    {
      label: "Requested Amount",
      key: "requested_amount",
      sortable: false,
      searchable: false,
      width: "13%",
      render: (rowData) => {
        return (
          <span>
            {rowData.requested_amount
              ? `ETB ${parseFloat(rowData.requested_amount).toLocaleString()}`
              : "N/A"}
          </span>
        );
      },
    },
    {
      label: "Term",
      key: "term",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (rowData) => {
        return <span>{rowData.term || "N/A"}</span>;
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <Badge color={getStatusColor(rowData.status)} variant="filled">
            {rowData.status || "N/A"}
          </Badge>
        );
      },
    },
    {
      label: "Workflow Status",
      key: "workflow_status",
      sortable: false,
      searchable: false,
      width: "15%",
      render: (rowData) => {
        return (
          <Badge
            color={getWorkflowStatusColor(rowData.workflow_status)}
            variant="light"
          >
            {rowData.workflow_status || "N/A"}
          </Badge>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        const isDisbursed =
          rowData.status === "DISBURSED" ||
          rowData.workflow_status === "DISBURSED";

        return (
          <div style={{ display: "flex", gap: "6px" }}>
            {isMaker && !isDisbursed && (
              <>
                <ActionButton
                  label="Accept"
                  color="success"
                  onClick={() => {
                    setApplicationToAccept(rowData);
                    setMakerRemark(
                      "Application reviewed and accepted for checker review.",
                    );
                    setAcceptModalOpened(true);
                  }}
                >
                  <CheckIcon style={{ fontSize: "1rem" }} />
                </ActionButton>
                <ActionButton
                  label="Reject"
                  color="error"
                  onClick={() => {
                    setApplicationToReject(rowData);
                    setRejectRemark(
                      "Required application documents are missing.",
                    );
                    setRejectModalOpened(true);
                  }}
                >
                  <CloseIcon style={{ fontSize: "1rem" }} />
                </ActionButton>
              </>
            )}
            {isChecker && rowData.workflow_status === "MAKER_ACCEPTED" && (
              <ActionButton
                label="Verify"
                color="success"
                onClick={() => {
                  setApplicationToVerify(rowData);
                  setCheckerRemark("Application verified.");
                  setVerifyModalOpened(true);
                }}
              >
                <CheckIcon style={{ fontSize: "1rem" }} />
              </ActionButton>
            )}
            {isChecker && rowData.workflow_status === "CHECKER_VERIFIED" && (
              <ActionButton
                label="Disburse"
                color="activate"
                onClick={() => {
                  setApplicationToDisburse(rowData);
                  setDisburseModalOpened(true);
                }}
              >
                <PaidIcon style={{ fontSize: "1rem" }} />
              </ActionButton>
            )}
            <ActionButton
              label="View"
              onClick={() => handleViewApplication(rowData)}
            >
              <VisibilityIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: null,
  };

  const handleViewApplication = (application) => {
    setOpened(true);
    setViewData(application);
    setActiveTab("details");
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="right"
        size="70%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        {viewData && (
          <div style={{ padding: "20px" }}>
            <h2 style={{ marginBottom: "20px" }}>
              Application: {viewData.trx}
            </h2>
            <Tabs value={activeTab} onTabChange={setActiveTab}>
              <Tabs.List>
                <Tabs.Tab value="details">Application Details</Tabs.Tab>
                <Tabs.Tab value="customer">Customer Profile</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="details" pt="md">
                <div style={{ display: "grid", gap: "15px" }}>
                  <div>
                    <strong>TRX:</strong> {viewData.trx}
                  </div>
                  <div>
                    <strong>Product:</strong> {viewData.product_name}
                  </div>
                  <div>
                    <strong>Borrower Type:</strong> {viewData.borrower_type}
                  </div>
                  <div>
                    <strong>Requested Amount:</strong> ETB{" "}
                    {parseFloat(viewData.requested_amount).toLocaleString()}
                  </div>
                  {viewData.approved_amount && (
                    <div>
                      <strong>Approved Amount:</strong> ETB{" "}
                      {parseFloat(viewData.approved_amount).toLocaleString()}
                    </div>
                  )}
                  <div>
                    <strong>Term:</strong> {viewData.term}
                  </div>
                  <div>
                    <strong>Purpose:</strong> {viewData.purpose || "N/A"}
                  </div>
                  <div>
                    <strong>Collateral Type:</strong>{" "}
                    {viewData.collateral_type || "N/A"}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    <Badge color={getStatusColor(viewData.status)}>
                      {viewData.status}
                    </Badge>
                  </div>
                  <div>
                    <strong>Workflow Status:</strong>{" "}
                    <Badge
                      color={getWorkflowStatusColor(viewData.workflow_status)}
                    >
                      {viewData.workflow_status}
                    </Badge>
                  </div>

                  {/* Credit Score Section */}
                  {(viewData.credit_score ||
                    viewData.risk_tier ||
                    viewData.recommended_loan_limit) && (
                    <>
                      <div
                        style={{
                          marginTop: "10px",
                          paddingTop: "15px",
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <h3
                          style={{ marginBottom: "10px", fontSize: "1.1rem" }}
                        >
                          Credit Assessment
                        </h3>
                      </div>
                      {viewData.credit_score && (
                        <div>
                          <strong>Credit Score:</strong>{" "}
                          <span
                            style={{
                              color:
                                viewData.credit_score >= 700
                                  ? "#2ecc71"
                                  : viewData.credit_score >= 600
                                    ? "#f39c12"
                                    : "#e74c3c",
                              fontWeight: 600,
                            }}
                          >
                            {viewData.credit_score}
                          </span>
                        </div>
                      )}
                      {viewData.risk_tier && (
                        <div>
                          <strong>Risk Tier:</strong>{" "}
                          <Badge
                            color={
                              viewData.risk_tier === "LOW"
                                ? "green"
                                : viewData.risk_tier === "MEDIUM"
                                  ? "yellow"
                                  : viewData.risk_tier === "HIGH"
                                    ? "orange"
                                    : "red"
                            }
                          >
                            {viewData.risk_tier}
                          </Badge>
                        </div>
                      )}
                      {viewData.recommended_loan_limit && (
                        <div>
                          <strong>Recommended Loan Limit:</strong> ETB{" "}
                          {parseFloat(
                            viewData.recommended_loan_limit,
                          ).toLocaleString()}
                        </div>
                      )}
                      {viewData.credit_score_confidence && (
                        <div>
                          <strong>Credit Score Confidence:</strong>{" "}
                          {viewData.credit_score_confidence}%
                        </div>
                      )}
                      {viewData.credit_score_reasons && (
                        <div>
                          <strong>Credit Score Factors:</strong>
                          <div
                            style={{
                              marginTop: "5px",
                              padding: "10px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "4px",
                              fontSize: "0.9rem",
                            }}
                          >
                            {viewData.credit_score_reasons}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Remarks Section */}
                  {viewData.review_remark && (
                    <div>
                      <strong>Review Remark:</strong> {viewData.review_remark}
                    </div>
                  )}
                  {viewData.maker_remark && (
                    <div>
                      <strong>Maker Remark:</strong> {viewData.maker_remark}
                    </div>
                  )}
                  {viewData.checker_remark && (
                    <div>
                      <strong>Checker Remark:</strong> {viewData.checker_remark}
                    </div>
                  )}
                  {viewData.approver_remark && (
                    <div>
                      <strong>Approver Remark:</strong>{" "}
                      {viewData.approver_remark}
                    </div>
                  )}
                  <div>
                    <strong>Created At:</strong>{" "}
                    {new Date(viewData.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Tabs.Panel>

              <Tabs.Panel value="customer" pt="md">
                {customerLoading ? (
                  <LoadingOverlay
                    visible={true}
                    overlayBlur={2}
                    loader={customLoader}
                  />
                ) : customerData?.loanCustomer ? (
                  <div style={{ display: "grid", gap: "20px" }}>
                    {/* Customer Basic Info */}
                    <div>
                      <h3 style={{ marginBottom: "10px", fontSize: "1.1rem" }}>
                        Basic Information
                      </h3>
                      <div style={{ display: "grid", gap: "10px" }}>
                        <div>
                          <strong>Name:</strong>{" "}
                          {customerData.loanCustomer.name}
                        </div>
                        <div>
                          <strong>User ID:</strong>{" "}
                          {customerData.loanCustomer.user_id}
                        </div>
                        <div>
                          <strong>Borrower Type:</strong>{" "}
                          {customerData.loanCustomer.borrower_type}
                        </div>
                      </div>
                    </div>

                    {/* Related Documents */}
                    {customerData.loanCustomer.related_documents &&
                      customerData.loanCustomer.related_documents.length >
                        0 && (
                        <div>
                          <h3
                            style={{ marginBottom: "10px", fontSize: "1.1rem" }}
                          >
                            Related Documents
                          </h3>
                          <div style={{ display: "grid", gap: "8px" }}>
                            {customerData.loanCustomer.related_documents.map(
                              (doc, index) => (
                                <div
                                  key={index}
                                  style={{
                                    padding: "10px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <strong>{doc.name}:</strong>{" "}
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "#1976d2" }}
                                  >
                                    View Document
                                  </a>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {/* Loan History */}
                    {customerData.loanCustomer.loan_history &&
                      customerData.loanCustomer.loan_history.length > 0 && (
                        <div>
                          <h3
                            style={{ marginBottom: "10px", fontSize: "1.1rem" }}
                          >
                            Loan History
                          </h3>
                          <ScrollArea>
                            <Table striped highlightOnHover>
                              <thead>
                                <tr>
                                  <th>TRX</th>
                                  <th>Requested</th>
                                  <th>Approved</th>
                                  <th>Term</th>
                                  <th>Status</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {customerData.loanCustomer.loan_history.map(
                                  (loan) => (
                                    <tr key={loan.id}>
                                      <td>{loan.trx}</td>
                                      <td>
                                        ETB{" "}
                                        {parseFloat(
                                          loan.requested_amount || 0,
                                        ).toLocaleString()}
                                      </td>
                                      <td>
                                        ETB{" "}
                                        {parseFloat(
                                          loan.approved_amount || 0,
                                        ).toLocaleString()}
                                      </td>
                                      <td>{loan.term}</td>
                                      <td>
                                        <Badge
                                          color={getStatusColor(loan.status)}
                                          size="sm"
                                        >
                                          {loan.status}
                                        </Badge>
                                      </td>
                                      <td>
                                        {new Date(
                                          loan.created_at,
                                        ).toLocaleDateString()}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </Table>
                          </ScrollArea>
                        </div>
                      )}

                    {(!customerData.loanCustomer.loan_history ||
                      customerData.loanCustomer.loan_history.length === 0) && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "gray",
                        }}
                      >
                        No loan history available
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "gray",
                    }}
                  >
                    Customer profile not available
                  </div>
                )}
              </Tabs.Panel>
            </Tabs>
          </div>
        )}
      </Drawer>

      <Modal
        opened={acceptModalOpened}
        onClose={() => {
          if (!acceptingLoan) {
            setAcceptModalOpened(false);
            setApplicationToAccept(null);
          }
        }}
        title="Accept Loan Application"
        centered
      >
        <Textarea
          label="Remark"
          placeholder="Add an optional remark"
          value={makerRemark}
          onChange={(event) => setMakerRemark(event.currentTarget.value)}
          minRows={3}
          mb="md"
        />
        <Group position="right">
          <Button
            variant="default"
            onClick={() => {
              setAcceptModalOpened(false);
              setApplicationToAccept(null);
            }}
            disabled={acceptingLoan}
          >
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleAcceptApplication}
            loading={acceptingLoan}
            disabled={!applicationToAccept}
          >
            Accept for Checker Review
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={rejectModalOpened}
        onClose={() => {
          if (!rejectingLoan) {
            setRejectModalOpened(false);
            setApplicationToReject(null);
          }
        }}
        title="Reject Loan Application"
        centered
      >
        <Textarea
          label="Remark"
          placeholder="Explain why this application is being rejected"
          value={rejectRemark}
          onChange={(event) => setRejectRemark(event.currentTarget.value)}
          minRows={3}
          required
          mb="md"
        />
        <Group position="right">
          <Button
            variant="default"
            onClick={() => {
              setRejectModalOpened(false);
              setApplicationToReject(null);
            }}
            disabled={rejectingLoan}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleRejectApplication}
            loading={rejectingLoan}
            disabled={!applicationToReject || !rejectRemark.trim()}
          >
            Reject Application
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={verifyModalOpened}
        onClose={() => {
          if (!verifyingLoan) {
            setVerifyModalOpened(false);
            setApplicationToVerify(null);
          }
        }}
        title="Verify Loan Application"
        centered
      >
        <Textarea
          label="Remark"
          placeholder="Add an optional verification remark"
          value={checkerRemark}
          onChange={(event) => setCheckerRemark(event.currentTarget.value)}
          minRows={3}
          mb="md"
        />
        <Group position="right">
          <Button
            variant="default"
            onClick={() => {
              setVerifyModalOpened(false);
              setApplicationToVerify(null);
            }}
            disabled={verifyingLoan}
          >
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleVerifyApplication}
            loading={verifyingLoan}
            disabled={!applicationToVerify}
          >
            Verify Application
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={disburseModalOpened}
        onClose={() => {
          if (!disbursingLoan) {
            setDisburseModalOpened(false);
            setApplicationToDisburse(null);
          }
        }}
        title="Disburse Loan"
        centered
      >
        <p>
          Disburse the loan for application{" "}
          <strong>
            {applicationToDisburse?.trx || applicationToDisburse?.id}
          </strong>
          ?
        </p>
        <Group position="right">
          <Button
            variant="default"
            onClick={() => {
              setDisburseModalOpened(false);
              setApplicationToDisburse(null);
            }}
            disabled={disbursingLoan}
          >
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleDisburseApplication}
            loading={disbursingLoan}
            disabled={!applicationToDisburse}
          >
            Confirm Disbursement
          </Button>
        </Group>
      </Modal>

      {/* Filters Section */}
      <Card shadow="sm" p="md" mb="md">
        <Group spacing="md" align="flex-end">
          <Select
            label="Status"
            placeholder="All Statuses"
            clearable
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setActivePage(1);
            }}
            data={[
              { value: "PENDING", label: "Pending" },
              { value: "UNDER_REVIEW", label: "Under Review" },
              { value: "MAKER_SUBMITTED", label: "Maker Submitted" },
              { value: "CHECKER_VERIFIED", label: "Checker Verified" },
              { value: "CHECKER_RETURNED", label: "Checker Returned" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
              { value: "DISBURSED", label: "Disbursed" },
            ]}
            style={{ minWidth: 200 }}
            withinPortal
          />
          <Select
            label="Workflow Status"
            placeholder="All Workflow Statuses"
            clearable
            value={workflowFilter}
            onChange={(value) => {
              setWorkflowFilter(value);
              setActivePage(1);
            }}
            data={[
              { value: "MAKER_SUBMITTED", label: "Maker Submitted" },
              { value: "CHECKER_VERIFIED", label: "Checker Verified" },
              { value: "CHECKER_RETURNED", label: "Checker Returned" },
              { value: "APPROVED", label: "Approved" },
              { value: "REJECTED", label: "Rejected" },
            ]}
            style={{ minWidth: 200 }}
            withinPortal
          />
        </Group>
      </Card>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanApplications.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            //     optionsData={optionsData}
            loading={loading}
            data={data ? data.loanApplications.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanApplications;
